import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Images } from "../../helpers/Images";
import "./Game.css";

const isMobile = window.innerWidth < 600;

const CANVAS_WIDTH = isMobile ? 800 : 800;
const CANVAS_HEIGHT = isMobile ? 550 : 350;
const GROUND_Y = isMobile ? 500 : 300;
const GROUND_HEIGHT = 60;
const PLAYER_X = 80;
const PLAYER_WIDTH = isMobile ? 150 : 100;
const PLAYER_HEIGHT = isMobile ? 150 : 100;
const GRAVITY = 0.65;
const JUMP_FORCE = -13;
const INITIAL_SPEED = 8;
const MAX_SPEED = 20;
const OBSTACLE_MIN_GAP = 280;
const OBSTACLE_MAX_GAP = 520;
const HIGH_SCORE_KEY = "wedo-zeko-run-highscore";

const createObstacle = (x, speed) => {
  const types = ["block", "tall", "double"];
  const type = types[Math.floor(Math.random() * types.length)];

  if (type === "tall") {
    return { x, width: 28, height: 52, type };
  }
  if (type === "double") {
    return { x, width: 48, height: 36, type, gap: 22 };
  }
  return { x, width: 32, height: 36, type: "block" };
};

const rectsOverlap = (a, b) =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;

const GameTemplate = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef({
    playerY: GROUND_Y - PLAYER_HEIGHT,
    velocityY: 0,
    isJumping: false,
    obstacles: [],
    clouds: [],
    groundOffset: 0,
    speed: INITIAL_SPEED,
    score: 0,
    frame: 0,
    gameOver: false,
    started: false,
    nextObstacleX: CANVAS_WIDTH + 200,
    runBob: 0,
  });

  const zekoImgRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(HIGH_SCORE_KEY);
    if (saved) setHighScore(parseInt(saved, 10) || 0);

    const img = new Image();
    img.src = Images.zeko;
    zekoImgRef.current = img;
  }, []);

  const resetGame = useCallback(() => {
    const state = stateRef.current;
    state.playerY = GROUND_Y - PLAYER_HEIGHT;
    state.velocityY = 0;
    state.isJumping = false;
    state.obstacles = [];
    state.clouds = Array.from({ length: 4 }, (_, i) => ({
      x: 120 + i * 200,
      y: 30 + Math.random() * 50,
      width: 60 + Math.random() * 40,
      speed: 0.3 + Math.random() * 0.4,
    }));
    state.groundOffset = 0;
    state.speed = INITIAL_SPEED;
    state.score = 0;
    state.frame = 0;
    state.gameOver = false;
    state.started = true;
    state.nextObstacleX = CANVAS_WIDTH + 200;
    state.runBob = 0;
    setScore(0);
    setGameOver(false);
    setStarted(true);
  }, []);

  const jump = useCallback(() => {
    const state = stateRef.current;
    if (state.gameOver) {
      resetGame();
      return;
    }
    if (!state.started) {
      resetGame();
      return;
    }
    if (!state.isJumping) {
      state.velocityY = JUMP_FORCE;
      state.isJumping = true;
    }
  }, [resetGame]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === "Space" || e.code === "ArrowUp") {
        e.preventDefault();
        jump();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [jump]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const drawGround = (offset) => {
      ctx.fillStyle = "#e8eaed";
      ctx.fillRect(0, GROUND_Y, CANVAS_WIDTH, GROUND_HEIGHT);

      ctx.strokeStyle = "#c11a83";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(CANVAS_WIDTH, GROUND_Y);
      ctx.stroke();

      ctx.strokeStyle = "#d0d3d8";
      ctx.lineWidth = 2;
      const dashSpacing = 40;
      for (
        let x = -((offset % dashSpacing) + dashSpacing);
        x < CANVAS_WIDTH;
        x += dashSpacing
      ) {
        ctx.beginPath();
        ctx.moveTo(x, GROUND_Y + 18);
        ctx.lineTo(x + 20, GROUND_Y + 18);
        ctx.stroke();
      }
    };

    const drawCloud = (cloud) => {
      ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
      const { x, y, width } = cloud;
      ctx.beginPath();
      ctx.arc(x, y, width * 0.3, 0, Math.PI * 2);
      ctx.arc(x + width * 0.35, y - 8, width * 0.35, 0, Math.PI * 2);
      ctx.arc(x + width * 0.7, y, width * 0.28, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawObstacle = (obs) => {
      const baseY = GROUND_Y - obs.height;

      if (obs.type === "double") {
        const blockW = 20;
        const blockH = obs.height;
        ctx.fillStyle = "#595c61";
        ctx.fillRect(obs.x, baseY, blockW, blockH);
        ctx.fillRect(obs.x + obs.gap, baseY, blockW, blockH);
        ctx.fillStyle = "#c11a83";
        ctx.fillRect(obs.x + 4, baseY + 4, blockW - 8, 8);
        ctx.fillRect(obs.x + obs.gap + 4, baseY + 4, blockW - 8, 8);
        return;
      }

      ctx.fillStyle = obs.type === "tall" ? "#2b2e33" : "#595c61";
      ctx.fillRect(obs.x, baseY, obs.width, obs.height);

      ctx.fillStyle = "#c11a83";
      ctx.fillRect(obs.x + 4, baseY + 4, obs.width - 8, 8);

      if (obs.type === "tall") {
        ctx.fillStyle = "#23aef4";
        ctx.fillRect(obs.x + 6, baseY + 16, obs.width - 12, 6);
      }
    };

    const drawPlayer = (y, bob) => {
      const bobOffset = stateRef.current.isJumping ? 0 : Math.sin(bob) * 3;
      const drawY = y + bobOffset;

      if (zekoImgRef.current?.complete) {
        ctx.drawImage(
          zekoImgRef.current,
          PLAYER_X,
          drawY,
          PLAYER_WIDTH,
          PLAYER_HEIGHT,
        );
      } else {
        ctx.fillStyle = "#c11a83";
        ctx.fillRect(PLAYER_X, drawY, PLAYER_WIDTH, PLAYER_HEIGHT);
      }
    };

    const getPlayerHitbox = (y) => ({
      x: PLAYER_X + 12,
      y: y + 8,
      width: PLAYER_WIDTH - 24,
      height: PLAYER_HEIGHT - 12,
    });

    const getObstacleHitbox = (obs) => {
      const baseY = GROUND_Y - obs.height;
      if (obs.type === "double") {
        const blockW = 20;
        return [
          {
            x: obs.x + 2,
            y: baseY + 2,
            width: blockW - 4,
            height: obs.height - 4,
          },
          {
            x: obs.x + obs.gap + 2,
            y: baseY + 2,
            width: blockW - 4,
            height: obs.height - 4,
          },
        ];
      }
      return [
        {
          x: obs.x + 2,
          y: baseY + 2,
          width: obs.width - 4,
          height: obs.height - 4,
        },
      ];
    };

    const tick = () => {
      const state = stateRef.current;

      const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
      gradient.addColorStop(0, "#dff3fc");
      gradient.addColorStop(1, "#f5f0f8");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      state.clouds.forEach((cloud) => {
        if (state.started && !state.gameOver) {
          cloud.x -= cloud.speed;
          if (cloud.x + cloud.width < 0) {
            cloud.x = CANVAS_WIDTH + Math.random() * 100;
            cloud.y = 20 + Math.random() * 60;
          }
        }
        drawCloud(cloud);
      });

      if (state.started && !state.gameOver) {
        state.frame += 1;
        state.runBob += 0.25;
        state.groundOffset += state.speed;
        state.score = Math.floor(state.frame / 6);
        state.speed = Math.min(MAX_SPEED, INITIAL_SPEED + state.score / 200);

        state.velocityY += GRAVITY;
        state.playerY += state.velocityY;

        const floorY = GROUND_Y - PLAYER_HEIGHT;
        if (state.playerY >= floorY) {
          state.playerY = floorY;
          state.velocityY = 0;
          state.isJumping = false;
        }

        if (
          state.obstacles.length === 0 ||
          state.nextObstacleX < CANVAS_WIDTH - 100
        ) {
          if (
            state.frame > 60 &&
            (state.obstacles.length === 0 ||
              state.obstacles[state.obstacles.length - 1].x <
                CANVAS_WIDTH - OBSTACLE_MIN_GAP)
          ) {
            const gap =
              OBSTACLE_MIN_GAP +
              Math.random() * (OBSTACLE_MAX_GAP - OBSTACLE_MIN_GAP);
            const lastX = state.obstacles.length
              ? state.obstacles[state.obstacles.length - 1].x
              : CANVAS_WIDTH;
            const newX = Math.max(CANVAS_WIDTH, lastX + gap);
            state.obstacles.push(createObstacle(newX, state.speed));
            state.nextObstacleX = newX + gap;
          }
        }

        state.obstacles.forEach((obs) => {
          obs.x -= state.speed;
        });
        state.obstacles = state.obstacles.filter(
          (obs) => obs.x + obs.width > -20,
        );

        const playerBox = getPlayerHitbox(state.playerY);
        for (const obs of state.obstacles) {
          const boxes = getObstacleHitbox(obs);
          for (const box of boxes) {
            if (rectsOverlap(playerBox, box)) {
              state.gameOver = true;
              setGameOver(true);
              const finalScore = state.score;
              setHighScore((prev) => {
                if (finalScore > prev) {
                  localStorage.setItem(HIGH_SCORE_KEY, String(finalScore));
                  return finalScore;
                }
                return prev;
              });
              break;
            }
          }
          if (state.gameOver) break;
        }

        setScore(state.score);
      }

      drawGround(state.groundOffset);
      state.obstacles.forEach(drawObstacle);
      drawPlayer(state.playerY, state.runBob);

      if (!state.started) {
        ctx.fillStyle = "rgba(26, 28, 32, 0.45)";
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        ctx.fillStyle = "#fff";
        ctx.font = "bold 28px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Zeko Run", CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 20);
        ctx.font = "16px Arial, sans-serif";
        ctx.fillText(
          "Press Space or Tap to Start",
          CANVAS_WIDTH / 2,
          CANVAS_HEIGHT / 2 + 16,
        );
      } else if (state.gameOver) {
        ctx.fillStyle = "rgba(26, 28, 32, 0.5)";
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        ctx.fillStyle = "#fff";
        ctx.font = "bold 26px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Game Over!", CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 30);
        ctx.font = "18px Arial, sans-serif";
        ctx.fillStyle = "#23aef4";
        ctx.fillText(
          `Score: ${state.score}`,
          CANVAS_WIDTH / 2,
          CANVAS_HEIGHT / 2 + 4,
        );
        ctx.fillStyle = "#fff";
        ctx.font = "14px Arial, sans-serif";
        ctx.fillText(
          "Press Space or Tap to Retry",
          CANVAS_WIDTH / 2,
          CANVAS_HEIGHT / 2 + 32,
        );
      }

      animationRef.current = requestAnimationFrame(tick);
    };

    if (stateRef.current.clouds.length === 0) {
      stateRef.current.clouds = Array.from({ length: 4 }, (_, i) => ({
        x: 120 + i * 200,
        y: 30 + Math.random() * 50,
        width: 60 + Math.random() * 40,
        speed: 0.3 + Math.random() * 0.4,
      }));
    }

    animationRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  return (
    <Box className="game-page">
      <Box className="game-hero">
        <h1>Zeko Run</h1>
        <p>
          Help Zeko dodge obstacles and rack up points! Jump with Space, Up
          Arrow, or a tap.
        </p>
      </Box>

      <Box className="game-wrapper">
        <Box className="game-hud">
          <p className="game-score">
            Score: <span>{score}</span>
          </p>
          <p className="game-high-score">Best: {highScore}</p>
        </Box>

        <Box className="game-canvas-container" ref={containerRef}>
          <canvas
            ref={canvasRef}
            className="game-canvas"
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            onClick={jump}
            onTouchStart={(e) => {
              e.preventDefault();
              jump();
            }}
            role="img"
            aria-label="Zeko Run game. Press space or tap to jump."
          />
        </Box>

        <p className="game-instructions">
          {!started && !gameOver && "Tap or press Space to start"}
          {started && !gameOver && "Keep running — speed increases over time!"}
          {gameOver && "Nice try! Tap or press Space to play again"}
        </p>
      </Box>
    </Box>
  );
};

export default GameTemplate;
